import { useState, useEffect, useRef, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import DualDatePicker from '../../components/common/DualDatePicker';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import useImpersonation from '../../hooks/useImpersonation';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useConfirm } from '../../components/common/ConfirmModal';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import AddChargeModal from '../../components/common/AddChargeModal';
import {
  MdPayments, MdCheckCircle, MdSearch, MdClose, MdRefresh,
  MdBarChart, MdAdd,
  MdReceipt, MdEmail, MdWarning, MdUpload,
  MdThumbUp, MdThumbDown, MdOpenInNew,
  MdExpandMore,
  MdChevronLeft, MdChevronRight, MdMoreHoriz,
} from 'react-icons/md';
import { TbCash, TbClock, TbAlertTriangle, TbPercentage } from 'react-icons/tb';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as feeSvc from '../../services/feeService';
import SuspendedAccessPanel from '../../components/common/SuspendedAccessPanel';
import { isClubSuspended, isClubCancelled } from '../../utils/clubError';
import { FEE_STATUS_BADGE, FEE_STATUS_LABEL } from '../../utils/feeStatus';
import { useReceivables } from '../../hooks/useReceivables';
import { buildCollectQueue, buildCollectWhatsAppMessage, filterQueueStudents } from '../../utils/collectQueue';
import { toWhatsAppPhone, openWhatsApp } from '../../utils/whatsapp';
import { FaWhatsapp } from 'react-icons/fa';
import { useCalendar } from '../../context/CalendarContext';
import useTheme from '../../context/ThemeContext';
import { useTenant } from '../../context/TenantContext';
import useClubVocab from '../../hooks/useClubVocab';
import { adToBs, formatAdMonth, adHint } from '../../utils/calendar';
import DualDate from '../../components/common/DualDate';
import RecordPaymentModal from '../../components/fees/RecordPaymentModal';

// BS month label ("Asar 2083") for a given AD "YYYY-MM". Samples mid-month (day 15)
// to match formatBsMonth's convention; reuses adToBs (no new date math).
const bsMonthLabel = (yearMonth) => {
  try { const b = adToBs(new Date(`${yearMonth}-15`)); return `${b.monthName} ${b.year}`; }
  catch { return yearMonth; }
};

// Kathmandu-local (UTC+5:45) current month "YYYY-MM". Shifting the epoch by the offset
// and reading UTC parts avoids the toISOString() UTC-boundary off-by-one at month end.
// Same pattern MonthPopover uses in DualDatePicker.
const kthNow     = new Date(Date.now() + (5 * 60 + 45) * 60 * 1000);
const THIS_MONTH = `${kthNow.getUTCFullYear()}-${String(kthNow.getUTCMonth() + 1).padStart(2, '0')}`;

// adHint (muted AD-month hint beside a BS month label) now lives in utils/calendar.

// Status pill colours/labels now come from utils/feeStatus.js (single source shared with
// Students.jsx, StudentFinanceTab.jsx, StudentDashboard.jsx and DemoView.jsx).
const STATUS_BADGE = FEE_STATUS_BADGE;

const METHOD_LABEL = { cash: 'Cash', bank: 'Bank Transfer', khalti: 'Khalti', esewa: 'eSewa', fonepay: 'Fonepay', other: 'Other' };

const STATUS_LABEL = FEE_STATUS_LABEL;

const LABEL_CLS = 'block text-xs font-medium text-muted mb-1.5';
const INPUT_CLS = 'w-full bg-card border border-divider rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary/30';

// ── Receipt modal ─────────────────────────────────────────────────────────────

const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString()}`;

// Kathmandu-local date — Intl applies the tz offset, so no manual UTC math.
const fmtKtmDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Asia/Kathmandu', day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Installment breakdown with running balance — shared by ReceiptModal and HistoryTab's
// expandable row, so a partial fee's "paid in parts" story reads identically everywhere.
function InstallmentList({ payments, totalAmount }) {
  let running = Number(totalAmount || 0);
  return (
    <div className="rounded-xl border border-divider divide-y divide-divider">
      {payments.map((pay, i) => {
        running = Math.max(0, running - (Number(pay.amount) || 0));
        return (
          <div key={pay._id || i} className="flex items-center justify-between gap-3 px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-secondary">{fmtNPR(pay.amount)}</p>
              <p className="text-[11px] text-muted">{fmtKtmDate(pay.paidAt)} · {METHOD_LABEL[pay.method] || pay.method || '—'}</p>
            </div>
            <span className="text-[11px] text-muted whitespace-nowrap">{running > 0 ? `NPR ${running.toLocaleString()} left` : 'Cleared'}</span>
          </div>
        );
      })}
    </div>
  );
}

// Load a (possibly remote) image → PNG data URL; resolves null on any failure so the PDF
// degrades to text-only gracefully. Same approach as utils/pdfExport.js.
const loadLogoDataUrl = (url) => new Promise((resolve) => {
  if (!url) return resolve(null);
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = url;
});

// Premium client-side receipt PDF (A5, clean white). jsPDF + autotable. Exported so the
// student-drawer Finance tab can reuse the exact same generator.
export async function downloadReceiptPdf(receipt, formatMonth) {
  const doc = new jsPDF({ unit: 'mm', format: 'a5' }); // 148 × 210 mm
  const pageW = doc.internal.pageSize.getWidth();   // 148
  const pageH = doc.internal.pageSize.getHeight();  // 210
  const M = 14;

  // Apple-Air ink palette — clean white receipt, black text, minimal accents.
  const INK = [29, 29, 31], BODY = [81, 81, 84], MUTED = [134, 134, 139], SUBTLE = [174, 174, 178];
  const DIVIDER = [227, 227, 230], LIGHTROW = [245, 245, 247], GREEN = [22, 163, 74];

  // ── A) Header · logo (top-left) ─────────────────────────────────────────────
  const s = 16, lx = M, ly = 14;
  const logo = await loadLogoDataUrl(receipt?.club?.logo);
  if (logo) {
    try {
      doc.saveGraphicsState();
      doc.roundedRect(lx, ly, s, s, 2.5, 2.5, null);
      doc.clip(); if (doc.discardPath) doc.discardPath();
      doc.addImage(logo.dataUrl, 'PNG', lx, ly, s, s);
      doc.restoreGraphicsState();
    } catch {
      try { doc.restoreGraphicsState(); } catch { /* ignore */ }
      try { doc.addImage(logo.dataUrl, 'PNG', lx, ly, s, s); } catch { /* skip */ }
    }
  } else {
    // TrainAxis dumbbell fallback mark: dark rounded tile, white weights, red centre bar.
    doc.setFillColor(20, 20, 22); doc.roundedRect(lx, ly, s, s, 2.5, 2.5, 'F');
    doc.setFillColor(255, 255, 255);
    doc.rect(lx + 3, ly + 5, 1.6, 6, 'F');            // left weight
    doc.rect(lx + s - 4.6, ly + 5, 1.6, 6, 'F');      // right weight
    doc.rect(lx + 4.6, ly + 7.2, s - 9.2, 1.6, 'F');  // connecting bar
    doc.setFillColor(200, 16, 46); doc.rect(lx + 6.7, ly + 7.2, 2.6, 1.6, 'F'); // red centre
  }

  // Club name + address + contact (under the logo)
  let hy = ly + s + 5;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...INK);
  doc.text(receipt?.club?.name || 'Club', M, hy);
  const addr = [receipt?.club?.address?.street, receipt?.club?.address?.city, receipt?.club?.address?.district]
    .filter(Boolean).join(', ');
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...MUTED);
  if (addr) { hy += 5; doc.text(addr, M, hy); }
  const contact = receipt?.club?.contactEmail || receipt?.club?.contactPhone || receipt?.club?.ownerPhone;
  if (contact) { hy += 4.5; doc.text(String(contact), M, hy); }
  if (receipt?.club?.pan) { hy += 4.5; doc.text(`PAN: ${receipt.club.pan}`, M, hy); }

  // ── B) Header · title / number / date (top-right) ───────────────────────────
  doc.setFont('helvetica', 'normal'); doc.setFontSize(21); doc.setTextColor(...INK);
  doc.text('Receipt', pageW - M, 20, { align: 'right' });
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  doc.text(String(receipt.receiptNumber || '—'), pageW - M, 26.5, { align: 'right' });
  // Dual-calendar paid date (BS + AD), computed in Asia/Kathmandu.
  const paid = receipt.paidDate || receipt.paidAt;
  const adDate = fmtKtmDate(paid);
  let bsDate = '';
  try {
    const kth = new Date(new Date(paid).getTime() + (5 * 60 + 45) * 60000);
    const bs = adToBs(new Date(kth.getUTCFullYear(), kth.getUTCMonth(), kth.getUTCDate()));
    bsDate = `${bs.monthName || ''} ${bs.day}, ${bs.year}`.trim();
  } catch { bsDate = ''; }
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...MUTED);
  if (bsDate) doc.text(bsDate, pageW - M, 31.5, { align: 'right' });
  doc.text(adDate, pageW - M, bsDate ? 35.5 : 31.5, { align: 'right' });

  // ── C) Divider ──────────────────────────────────────────────────────────────
  const dividerY = Math.max(hy + 4, 40);
  doc.setDrawColor(...DIVIDER); doc.setLineWidth(0.3); doc.line(M, dividerY, pageW - M, dividerY);

  // ── D) Billed to ────────────────────────────────────────────────────────────
  let by = dividerY + 8;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  doc.setCharSpace(0.5); doc.text('BILLED TO', M, by); doc.setCharSpace(0);
  by += 6;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...INK);
  doc.text(receipt?.studentId?.name || 'Student', M, by);
  const idLine = [receipt?.studentProfile?.studentId, receipt?.studentProfile?.currentGrade].filter(Boolean).join(' · ');
  if (idLine) {
    by += 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...MUTED);
    doc.text(idLine, M, by);
  }

  // ── E) Line items table ─────────────────────────────────────────────────────
  const subtitle = receipt.notes || '';
  // A custom charge has no billing period — its "period" is the day it was raised. Show that
  // creation date in BS + AD (Asia/Kathmandu) instead of a dash. Monthly rows are unchanged.
  let customPeriod = '—';
  if (receipt.type === 'custom') {
    const created = receipt.createdAt || receipt.paidDate || receipt.paidAt;
    const adC = fmtKtmDate(created);
    let bsC = '';
    try {
      const kthC = new Date(new Date(created).getTime() + (5 * 60 + 45) * 60000);
      const bsn = adToBs(new Date(kthC.getUTCFullYear(), kthC.getUTCMonth(), kthC.getUTCDate()));
      bsC = `${bsn.monthName || ''} ${bsn.day}, ${bsn.year}`.trim();
    } catch { bsC = ''; }
    customPeriod = bsC ? `${bsC}\n${adC}` : adC;
  }
  let headBottom = 0;
  autoTable(doc, {
    startY: by + 8,
    head: [['DESCRIPTION', 'PERIOD', 'AMOUNT']],
    body: [[
      receipt.type === 'custom'
        ? `${receipt.description || 'Charge'} (Custom charge)`
        : `Monthly Training Fee${receipt.isProrated ? ' (Partial)' : ''}`,
      receipt.type === 'custom'
        ? customPeriod
        : `${formatMonth(receipt.month)}${receipt.isProrated && receipt.billableDays ? ` (${receipt.billableDays} days)` : ''} · ${adHint(receipt.month)}`,
      fmtNPR(receipt.amount),
    ]],
    theme: 'plain',
    margin: { left: M, right: M },
    styles: { fontSize: 10, textColor: INK, valign: 'top', cellPadding: { top: 3, bottom: subtitle ? 7 : 3 } },
    headStyles: { fontSize: 7.5, fontStyle: 'normal', textColor: MUTED, cellPadding: { top: 0, bottom: 2.5 } },
    columnStyles: { 0: { halign: 'left' }, 1: { halign: 'center' }, 2: { halign: 'right' } },
    didParseCell: (d) => {
      if (d.section === 'head') d.cell.styles.textColor = MUTED;
      if (d.section === 'body' && d.column.index === 1) { d.cell.styles.fontSize = 9; d.cell.styles.textColor = BODY; }
      if (d.section === 'body' && d.column.index === 2) d.cell.styles.fontStyle = 'bold';
    },
    didDrawCell: (d) => {
      if (d.section === 'head' && d.column.index === 0) headBottom = d.cell.y + d.cell.height;
      if (d.section === 'body' && d.column.index === 0 && subtitle) {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...MUTED);
        doc.text(subtitle, d.cell.x, d.cell.y + d.cell.height - 3, { maxWidth: d.cell.width - 2 });
      }
    },
  });
  // Header underline + a light separator under the row.
  doc.setDrawColor(...DIVIDER); doc.setLineWidth(0.3); doc.line(M, headBottom, pageW - M, headBottom);
  const tableBottom = doc.lastAutoTable.finalY;
  doc.setDrawColor(...LIGHTROW); doc.setLineWidth(0.2); doc.line(M, tableBottom, pageW - M, tableBottom);

  // ── F) Totals (right-aligned) ───────────────────────────────────────────────
  const labelX = pageW - M - 42, valX = pageW - M;
  let ty = tableBottom + 7;
  const totals = [
    ['Subtotal', fmtNPR(receipt.amount)],
    ['Discount', receipt.discount > 0 ? `- ${fmtNPR(receipt.discount)}` : '—'],
  ];
  if (receipt.lateFee > 0) totals.push(['Late fee', `+ ${fmtNPR(receipt.lateFee)}`]);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  totals.forEach(([label, val]) => {
    doc.setTextColor(...MUTED); doc.text(label, labelX, ty, { align: 'right' });
    doc.setTextColor(...BODY);  doc.text(val, valX, ty, { align: 'right' });
    ty += 5.5;
  });
  ty += 1;
  doc.setDrawColor(...INK); doc.setLineWidth(0.5); doc.line(pageW - M - 55, ty, valX, ty);
  ty += 6.5;
  doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
  doc.setFontSize(12); doc.text('Total paid', labelX, ty, { align: 'right' });
  doc.setFontSize(13); doc.text(fmtNPR(receipt.totalAmount || receipt.amount), valX, ty, { align: 'right' });

  // ── G) Payment info (left) ──────────────────────────────────────────────────
  let py = ty + 14;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  doc.setCharSpace(0.4); doc.text('PAID VIA', M, py); doc.setCharSpace(0);
  py += 5;
  doc.setFontSize(10); doc.setTextColor(...INK);
  doc.text(METHOD_LABEL[receipt.method] || receipt.method || '—', M, py);
  py += 5;
  doc.setFontSize(9); doc.setTextColor(...GREEN);
  doc.text('Paid', M, py);

  // ── H) Footer (page bottom) ─────────────────────────────────────────────────
  const fy = pageH - 12;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  doc.text('Thank you for your payment', M, fy);
  doc.setTextColor(...SUBTLE);
  doc.text('Powered by TrainAxis', pageW - M, fy, { align: 'right' });

  // Generated-on stamp (the download date, Kathmandu) — distinct from the payment date above.
  const nowKth = new Date(Date.now() + (5 * 60 + 45) * 60000);
  let genBs = '';
  try {
    const bsN = adToBs(new Date(nowKth.getUTCFullYear(), nowKth.getUTCMonth(), nowKth.getUTCDate()));
    genBs = `${bsN.monthName || ''} ${bsN.day}, ${bsN.year}`.trim();
  } catch { genBs = ''; }
  doc.setFontSize(6.5); doc.setTextColor(...SUBTLE);
  doc.text(`Generated on ${genBs ? `${genBs} · ` : ''}${fmtKtmDate(new Date())}`, M, pageH - 7);

  doc.save(`receipt-${receipt.receiptNumber || receipt._id}.pdf`);
}

function ReceiptRow({ label, value, valueClass = 'text-secondary' }) {
  return (
    <tr>
      <td className="py-1.5 text-muted align-top whitespace-nowrap pr-3">{label}</td>
      <td className={`py-1.5 text-right font-medium ${valueClass}`}>{value}</td>
    </tr>
  );
}

function ReceiptModal({ paymentId, onClose }) {
  useEscapeKey(onClose);
  const v = useClubVocab();
  const { isReadOnly } = useImpersonation();
  const { formatMonth } = useCalendar();
  const [emailing,    setEmailing]    = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['receipt', paymentId],
    queryFn:  () => feeSvc.getReceipt(paymentId).then(r => r.data),
    enabled:  !!paymentId,
  });
  const receipt      = data?.receipt || null;
  const brandColor   = receipt?.club?.primaryColor || '#E24B4A';
  const paidDate     = receipt?.paidDate || receipt?.paidAt || null;
  const memberId     = receipt?.studentProfile?.studentId || null;
  const studentName  = receipt?.studentId?.name || v.member;
  const studentEmail = receipt?.studentId?.email || null;
  const recordedBy   = receipt?.recordedBy?.name || receipt?.confirmedBy?.name || null;
  const proofUrl     = receipt?.transferScreenshot || receipt?.proofUrl || null;

  const handleEmail = async () => {
    setEmailing(true);
    try {
      const r = await feeSvc.sendReceiptEmail(paymentId);
      toast.success(r.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send receipt');
    } finally {
      setEmailing(false);
    }
  };

  const handleDownload = async () => {
    if (!receipt) return;
    setDownloading(true);
    try {
      await downloadReceiptPdf(receipt, formatMonth);
    } catch (err) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto border border-divider">
        {/* Header band — accented with the club's brand colour */}
        <div className="px-6 py-5 rounded-t-2xl flex items-center justify-between" style={{ backgroundColor: brandColor }}>
          <div className="flex items-center gap-3 min-w-0">
            {receipt?.club?.logo ? (
              <img src={receipt.club.logo} alt="" className="w-10 h-10 rounded-lg object-cover bg-white/20 flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <MdReceipt className="text-white text-xl" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-white font-bold text-base leading-tight truncate">{receipt?.club?.name || 'Receipt'}</p>
              <p className="text-white/80 text-xs tracking-wide uppercase mt-0.5">Payment Receipt</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:bg-white/15 flex-shrink-0">
            <MdClose className="text-xl" />
          </button>
        </div>

        {/* Body — never a blank flash: skeleton while loading, clear error state otherwise */}
        {isPending ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-5 bg-nav-hover/50 rounded animate-pulse" />)}
          </div>
        ) : isError || !receipt ? (
          <div className="p-8 text-center">
            <MdWarning className="mx-auto text-3xl text-amber-500 mb-2" />
            <p className="text-sm font-semibold text-secondary">Couldn't load the receipt</p>
            <p className="text-xs text-muted mt-1 mb-4">There was an error fetching this receipt.</p>
            <button onClick={() => refetch()}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-divider text-secondary hover:bg-nav-hover transition-colors">
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="px-6 pt-5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-muted uppercase tracking-wide">Receipt No.</span>
                {receipt.type === 'custom' && (
                  <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-nav-hover text-muted border border-divider">Custom charge</span>
                )}
              </div>
              <span className="text-sm font-bold text-secondary font-mono">{receipt.receiptNumber || '—'}</span>
            </div>

            <div className="px-6 py-4">
              <table className="w-full text-sm">
                <tbody>
                  <ReceiptRow label={v.member} value={<span>{studentName}{memberId && <span className="text-muted"> · {memberId}</span>}</span>} />
                  {receipt.type === 'custom' ? (
                    <ReceiptRow label="Charge" value={receipt.description || 'Custom charge'} />
                  ) : (
                    <ReceiptRow label="Period"  value={
                      <span>
                        {formatMonth(receipt.month)}
                        {receipt.isProrated && receipt.billableDays ? ` (${receipt.billableDays} days)` : ''}
                        <span className="text-muted"> · {adHint(receipt.month)}</span>
                        {receipt.isProrated && (
                          <span className="ml-1.5 inline-block text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 align-middle">Partial</span>
                        )}
                      </span>
                    } />
                  )}
                  <ReceiptRow label="Date Paid" value={fmtKtmDate(paidDate)} />
                  <ReceiptRow label="Method"  value={METHOD_LABEL[receipt.method] || receipt.method || '—'} />
                  <tr><td colSpan={2} className="pt-1"><div className="border-t border-divider" /></td></tr>
                  <ReceiptRow label={receipt.type === 'custom' ? 'Charge Amount' : 'Base Fee'} value={fmtNPR(receipt.amount)} />
                  {receipt.lateFee > 0  && <ReceiptRow label="Late Fee" value={`+ ${fmtNPR(receipt.lateFee)}`} valueClass="text-red-400" />}
                  {receipt.discount > 0 && <ReceiptRow label="Discount" value={`- ${fmtNPR(receipt.discount)}`} valueClass="text-emerald-400" />}
                </tbody>
              </table>

              <div className="flex items-center justify-between mt-3 pt-3 border-t-2 border-divider">
                <span className="text-sm font-bold text-secondary">Total Paid</span>
                <span className="text-lg font-bold text-secondary">{fmtNPR(receipt.totalAmount || receipt.amount)}</span>
              </div>
            </div>

            {/* Payment history — installments when the fee was paid in parts, with running balance. */}
            {Array.isArray(receipt.payments) && receipt.payments.length > 0 && (
              <div className="px-6 pb-4">
                <p className="text-xs font-semibold text-muted mb-2">Payment history</p>
                <InstallmentList payments={receipt.payments} totalAmount={receipt.totalAmount ?? receipt.amount ?? 0} />
              </div>
            )}

            {(recordedBy || receipt.club?.contactPhone || proofUrl) && (
              <div className="px-6 pb-4 text-xs text-muted space-y-1">
                {recordedBy && <p>Recorded by {recordedBy}</p>}
                {receipt.club?.contactPhone && <p>{receipt.club.contactPhone}</p>}
                {proofUrl && (
                  <a href={proofUrl} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-secondary hover:underline font-medium">
                    <MdOpenInNew className="text-sm" /> View proof
                  </a>
                )}
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex flex-wrap justify-end gap-2 px-6 py-4 border-t border-divider">
          {studentEmail && (
            <Button variant="ghost" onClick={handleEmail} disabled={emailing || isPending || isError || isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}>
              <MdEmail /> {emailing ? 'Sending…' : `Email to ${v.member.toLowerCase()}`}
            </Button>
          )}
          <Button variant="ghost" onClick={handleDownload} disabled={downloading || isPending || isError || !receipt}>
            {downloading ? 'Preparing…' : 'Download PDF'}
          </Button>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Overview ─────────────────────────────────────────────────────────────

function OverviewTab({ month, onMonthChange }) {
  const currentTheme = useTheme()?.currentTheme;
  const v = useClubVocab();
  const { isReadOnly } = useImpersonation();
  const qc = useQueryClient();
  const [generating,       setGenerating]       = useState(false);
  const [sendingReminders, setSendingReminders]  = useState(false);

  const { data, isPending, refetch, error } = useQuery({
    queryKey: ['fee-summary', month],
    queryFn:  () => feeSvc.getPaymentSummary(month).then(r => r.data),
  });

  const { data: feeStructure } = useQuery({
    queryKey: ['fee-structure'],
    queryFn:  () => feeSvc.getFeeStructure().then(r => r.data),
    staleTime: 1000 * 60 * 5,
  });

  const feeNotConfigured = !isPending && feeStructure && !(feeStructure.structure?.monthlyFee > 0);

  const s = data?.summary;
  // Money-based collection rate (Stage 2 API): collected ÷ charged for the month, so a
  // half-paid student counts as half-collected — not fully unpaid. Falls back to the old
  // count-based ratio if the API hasn't been deployed yet.
  const paidPct = s
    ? (s.collectionRate != null
        ? s.collectionRate
        : (s.totalStudents > 0 ? Math.round((s.completed / s.totalStudents) * 100) : 0))
    : 0;

  const bsLabel = bsMonthLabel(month);   // "Asar 2083"
  const adLabel = formatAdMonth(month);  // "Jun 2026"

  // Step the AD month by ±1, keeping `month` as "YYYY-MM" (all logic unchanged).
  const stepMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const [confirm, confirmModal] = useConfirm();
  const handleGenerate = async () => {
    if (!(await confirm({ title: 'Generate fees?', message: `Generate fees for ${bsLabel} (${adLabel})? This creates a record for each active ${v.member.toLowerCase()} who doesn't have one yet.`, confirmLabel: 'Generate', destructive: false }))) return;
    setGenerating(true);
    try {
      const r = await feeSvc.generateMonthlyFees(month);
      const created = r.data.created || 0;
      const skipped = r.data.skipped || 0;
      const total   = created + skipped;
      if (created === 0 && total > 0) {
        // Already generated — neutral/info, not an error.
        toast(`Fees for ${bsLabel} are already generated — all ${total} ${v.members.toLowerCase()} have records.`, { icon: 'ℹ️' });
      } else if (created > 0) {
        toast.success(`Generated fees for ${created} ${created !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()} for ${bsLabel}.`);
      } else {
        toast(r.data.message || `No active ${v.members.toLowerCase()} to generate fees for.`);
      }
      refetch();
      qc.invalidateQueries({ queryKey: ['fee-payments'] });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate fees');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const r = await feeSvc.sendAllReminders(month);
      toast.success(r.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  // "Past Due" tile is deliberately NOT month-scoped — debt isn't a property of a month, so this
  // reads s.overdueAllTime (every literal status==='overdue' fee, any month) instead of
  // s.overdue/s.overdueAmount (this month's overdue rows only). A small "(all-time)" sub-caption
  // marks it as intentionally different from the three month-scoped tiles beside it.
  // Labelled "past due", not "outstanding" — "outstanding" is reserved for total unpaid balance,
  // due or not (the Collect/Receivables meaning), a much broader number than this narrow,
  // literal-status count; using the same word for both reads as a contradiction between pages.
  const summaryCards = s ? [
    { label: 'Fees Collected',  value: `NPR ${s.totalCollected.toLocaleString()}`, sub: `${s.completed} payment${s.completed !== 1 ? 's' : ''}`,                 Icon: TbCash,           tile: 'bg-green-100 text-green-600' },
    { label: 'Pending',         value: String(s.pending),                          sub: `${s.pending !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()} yet to pay`,                       Icon: TbClock,          tile: 'bg-amber-100 text-amber-600' },
    { label: 'Past Due',        value: String(s.overdueAllTime?.count || 0),       sub: s.overdueAllTime?.count > 0 ? `NPR ${s.overdueAllTime.amount.toLocaleString()} past due (all-time)` : 'none past due', Icon: TbAlertTriangle, tile: 'bg-red-100 text-red-600' },
    { label: 'Collection rate', value: `${paidPct}%`,                              sub: `${s.completed} of ${s.totalStudents} ${v.members.toLowerCase()}`,                         Icon: TbPercentage,     tile: 'bg-blue-100 text-blue-600' },
  ] : [];

  // Nothing generated for the selected month yet — distinct from "generated and all paid".
  // Showing zeroed tiles here reads as a collection failure; show the real reason instead.
  const feesNotGenerated = !isPending && s && s.feesGenerated === false;

  // Club access blocked (subscription suspended/expired) — clear panel, not empty summary.
  if (isClubSuspended(error)) return <SuspendedAccessPanel />;
  if (isClubCancelled(error)) return <SuspendedAccessPanel cancelled />;

  return (
    <div className="space-y-4">
      {confirmModal}
      {/* Fee not configured tip */}
      {feeNotConfigured && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <MdWarning className="text-amber-500 text-lg flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">Monthly fee not set yet</p>
            <p className="text-xs text-amber-700 mt-0.5">Go to the <strong>Fee settings</strong> tab and enter the amount members pay each month before generating fees.</p>
          </div>
        </div>
      )}

      {/* Month bar: BS·AD pill with chevrons (left), actions (right) */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-card border border-divider rounded-xl px-2.5 py-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => stepMonth(-1)}
            aria-label="Previous month"
            className="w-10 h-10 rounded-full border border-divider text-muted hover:text-secondary hover:bg-nav-hover flex items-center justify-center transition-colors"
          >
            <MdChevronLeft size={18} />
          </button>
          <div className="px-3 text-center min-w-[150px]">
            <span className="text-sm font-semibold text-secondary">{bsLabel}</span>
            <span className="text-subtle mx-1.5">·</span>
            <span className="text-xs text-muted">{adLabel}</span>
          </div>
          <button
            onClick={() => stepMonth(1)}
            aria-label="Next month"
            className="w-10 h-10 rounded-full border border-divider text-muted hover:text-secondary hover:bg-nav-hover flex items-center justify-center transition-colors"
          >
            <MdChevronRight size={18} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSendReminders}
            disabled={sendingReminders || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className="flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-full bg-card border border-divider text-secondary hover:bg-secondary hover:text-card hover:border-secondary disabled:opacity-50 transition-all duration-200"
          >
            <MdEmail className={sendingReminders ? 'animate-pulse' : ''} size={16} />
            {sendingReminders ? 'Sending…' : 'Send reminders'}
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className={`flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 disabled:opacity-50 transition-all duration-200`}
          >
            <MdRefresh className={generating ? 'animate-spin' : ''} size={16} />
            {generating ? 'Generating…' : 'Generate fees'}
          </button>
        </div>
      </div>

      {/* Summary cards — or, if nothing's been generated this month, say so plainly */}
      {feesNotGenerated ? (
        <div className="bg-card border border-divider rounded-xl p-6 text-center">
          <MdWarning className="text-3xl text-amber-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-secondary mb-1">No fees generated for {bsLabel} yet</p>
          <p className="text-xs text-muted mb-4">Generate this month's fees to start tracking collection.</p>
          <button
            onClick={handleGenerate}
            disabled={generating || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className={`inline-flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 disabled:opacity-50 transition-all duration-200`}
          >
            <MdRefresh className={generating ? 'animate-spin' : ''} size={16} />
            {generating ? 'Generating…' : 'Generate fees'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {isPending ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-[92px] bg-card border border-divider animate-pulse rounded-xl" />
            ))
          ) : summaryCards.map(({ label, value, sub, Icon, tile }) => (
            <div key={label} className="bg-card border border-divider rounded-xl p-3.5">
              <div className="flex items-center gap-2">
                <span className={`w-[26px] h-[26px] rounded-lg flex items-center justify-center flex-shrink-0 ${tile}`}>
                  <Icon size={15} />
                </span>
                <span className="text-xs text-muted font-medium">{label}</span>
              </div>
              <p className="text-2xl font-bold text-secondary mt-2 leading-none">{value}</p>
              <p className="text-xs text-muted mt-1.5">{sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Collection progress */}
      {!isPending && s && !feesNotGenerated && (
        <div className="bg-card border border-divider rounded-xl p-3.5 space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary">Collection progress</span>
              <span className="text-sm font-semibold text-secondary">{s.completed} / {s.totalStudents} {v.members.toLowerCase()}</span>
            </div>
            <div className="w-full h-2 rounded-full bg-nav-hover overflow-hidden mt-2">
              <div
                className="h-full rounded-full bg-primary transition-all duration-700"
                style={{ width: `${paidPct}%` }}
              />
            </div>
            <p className="text-xs text-muted mt-1.5">{paidPct}% collected</p>
          </div>

          {s.overdue > 0 && (
            <div className="flex items-center gap-3 rounded-lg bg-rose-500/10 border border-rose-500/20 px-3 py-2.5">
              <TbAlertTriangle className="text-rose-500 flex-shrink-0" size={18} />
              <p className="text-xs text-rose-600 flex-1 min-w-0">
                {s.overdue} {s.overdue !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()} overdue this month · NPR {s.overdueAmount.toLocaleString()} unpaid
              </p>
              <button
                onClick={handleSendReminders}
                disabled={sendingReminders || isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className="text-xs font-medium px-3 py-1.5 rounded-full bg-card border border-divider text-secondary hover:bg-secondary hover:text-card hover:border-secondary disabled:opacity-50 transition-all duration-200 flex-shrink-0"
              >
                {sendingReminders ? 'Sending…' : 'Remind overdue'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab: Collect — the working page. One row per student who owes money, worst-overdue
// first, with a Record Payment action on every row. Reuses the exact same receivables data
// Receivables.jsx fetches (GET /api/finance/receivables, via the shared useReceivables hook)
// — no new balance/aging computation, just a client-side group-by-student (buildCollectQueue).
// Mobile-first: this is what gets used on a phone at the dojang when cash changes hands.
// "All" must mean all — no daysOverdue floor. A fee already generated but not yet due has a
// NEGATIVE daysOverdue (see receivablesAging.js); a `min: 0` floor here silently excluded every
// such student from the "All" tab while the header above it (computed from the unfiltered
// queue) still counted them — the two numbers could never be trusted to agree. The other tiers
// are genuine overdue thresholds and keep theirs.
const OVERDUE_TIERS = [
  { key: 'all', label: 'All',  min: -Infinity },
  { key: '30',  label: '30+',  min: 30 },
  { key: '60',  label: '60+',  min: 60 },
  { key: '90',  label: '90+',  min: 90 },
];

const overdueColorCls = (days) =>
  days > 60 ? 'text-red-500' : days > 30 ? 'text-orange-500' : days > 0 ? 'text-amber-600' : 'text-muted';

function CollectTab() {
  const currentTheme   = useTheme()?.currentTheme;
  const { isReadOnly } = useImpersonation();
  const { club }       = useTenant();
  const qc             = useQueryClient();
  const { data, isLoading, error } = useReceivables();
  const [search,   setSearch]   = useState('');
  const [tierKey,  setTierKey]  = useState('all');
  const [recordModal, setRecord] = useState(null); // null | 'new' | { studentId }
  const [showCharge, setShowCharge] = useState(false);

  const { data: chargeStudentsData } = useQuery({
    queryKey: ['fee-students'],
    queryFn:  () => feeSvc.getStudentsForPayment().then(r => r.data),
  });
  const chargeStudents = chargeStudentsData?.students || [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['receivables'] });
    qc.invalidateQueries({ queryKey: ['fee-payments'] });
    qc.invalidateQueries({ queryKey: ['fee-summary'] });
    qc.invalidateQueries({ queryKey: ['student-unpaid-months'] });
  };

  const queue = buildCollectQueue(data);
  const activeTier = OVERDUE_TIERS.find(t => t.key === tierKey) || OVERDUE_TIERS[0];
  const filtered = filterQueueStudents(queue.students, { minDays: activeTier.min, search });
  // The header below always states the TRUE unfiltered total/count — it must never go quiet
  // about money a filter is hiding. When a tier/search narrows what's rendered, it says so
  // plainly alongside the real number, rather than letting the two silently disagree again.
  const isNarrowed = filtered.length !== queue.studentCount;
  const narrowedLabel = activeTier.key !== 'all' ? `overdue ${activeTier.label}` : (search ? 'matching your search' : null);

  if (isClubSuspended(error)) return <SuspendedAccessPanel />;
  if (isClubCancelled(error)) return <SuspendedAccessPanel cancelled />;

  return (
    <div className="space-y-4">
      {/* Totals + primary actions */}
      <div className="bg-card border border-divider rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-muted font-medium">Total outstanding</p>
          <p className="text-2xl font-bold text-secondary">NPR {queue.totalOutstanding.toLocaleString()}</p>
          <p className="text-xs text-muted mt-0.5">
            {queue.studentCount} {queue.studentCount !== 1 ? 'students' : 'student'} owe money
            {isNarrowed && ` · showing ${filtered.length}${narrowedLabel ? ` ${narrowedLabel}` : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCharge(true)}
            disabled={isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className="bg-card text-secondary border border-divider rounded-full font-medium text-sm px-3.5 py-2 hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Charge
          </button>
          <button
            onClick={() => setRecord('new')}
            disabled={isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className={`flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 disabled:opacity-50 transition-all duration-200`}
          >
            <MdAdd /> Record payment
          </button>
        </div>
      </div>

      {/* Search + overdue-tier filter */}
      <div className="bg-card border border-divider rounded-xl p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-card border border-divider text-secondary placeholder-subtle focus:outline-none focus:border-secondary transition-colors"
            />
          </div>
          <div className="flex items-center gap-1.5">
            {OVERDUE_TIERS.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTierKey(t.key)}
                aria-pressed={tierKey === t.key}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  tierKey === t.key ? 'bg-nav-hover text-secondary border-secondary' : 'bg-card text-muted border-divider hover:bg-nav-hover'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Queue */}
      <div className="bg-card border border-divider rounded-xl overflow-hidden divide-y divide-divider">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-4 space-y-2">
              <div className="h-4 w-32 bg-nav-hover rounded animate-pulse" />
              <div className="h-3 w-48 bg-nav-hover rounded animate-pulse" />
            </div>
          ))
        ) : !filtered.length ? (
          <div className="py-14 text-center px-4">
            <MdCheckCircle className="text-4xl text-subtle mx-auto mb-3" />
            <p className="text-sm font-semibold text-secondary mb-1">
              {queue.students.length === 0 ? 'Nobody owes money right now' : 'No matches'}
            </p>
            <p className="text-xs text-muted">
              {queue.students.length === 0 ? 'Every fee is paid up.' : 'Try a different search or overdue filter.'}
            </p>
          </div>
        ) : filtered.map(s => (
          <div key={s.studentId} className="p-4 flex items-start justify-between gap-3 hover:bg-nav-hover transition-colors">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-secondary">{s.studentName}</p>
                {s.studentTxId && <span className="text-[11px] text-subtle font-mono">{s.studentTxId}</span>}
              </div>
              <p className={`text-xs mt-0.5 ${overdueColorCls(s.daysOverdue)}`}>
                {s.daysOverdue > 0 ? `${s.daysOverdue} days overdue` : 'Not yet due'}
                <span className="text-muted"> · {s.unpaidMonths.length} unpaid {s.unpaidMonths.length !== 1 ? 'items' : 'item'}</span>
              </p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {s.unpaidMonths.slice(0, 4).map(m => (
                  <span key={m.feePaymentId} className={`text-[10px] px-1.5 py-0.5 rounded-md border ${m.isProrated ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-nav-hover text-muted border-divider'}`}>
                    {m.type === 'custom' ? (m.description || 'Custom charge') : m.feeMonthLabel}
                    {m.isProrated && m.billableDays ? ` (${m.billableDays} days)` : ''}
                    {m.isProrated && <span className="font-semibold"> · Partial</span>}
                  </span>
                ))}
                {s.unpaidMonths.length > 4 && (
                  <span className="text-[10px] text-subtle self-center">+{s.unpaidMonths.length - 4} more</span>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-base font-bold text-secondary">NPR {s.totalOwed.toLocaleString()}</p>
              <div className="flex items-center gap-1.5 mt-2 justify-end">
                {toWhatsAppPhone(s.studentPhone) && (
                  <button
                    onClick={() => openWhatsApp(s.studentPhone, buildCollectWhatsAppMessage(s, club?.name))}
                    title="Send WhatsApp reminder"
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-divider text-[#25D366] hover:bg-nav-hover transition-colors"
                  >
                    <FaWhatsapp className="text-sm" />
                  </button>
                )}
                <button
                  onClick={() => setRecord({ studentId: s.studentId })}
                  disabled={isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Record payment
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {recordModal && (
        <RecordPaymentModal
          defaultPayment={recordModal === 'new' ? null : recordModal}
          onClose={() => setRecord(null)}
          onSuccess={() => { setRecord(null); invalidate(); }}
        />
      )}
      {showCharge && (
        <AddChargeModal
          students={chargeStudents}
          onClose={() => setShowCharge(false)}
          onCreated={invalidate}
        />
      )}
    </div>
  );
}

// ── Row overflow menu (secondary actions) ──────────────────────────────────────
// Renders the dropdown with fixed positioning (computed from the trigger's rect) so
// it is never clipped by the table card's overflow-hidden. Closes on outside-click,
// Escape, selection, and scroll/resize. Items are buttons (keyboard-focusable).
function RowActionMenu({ items, disabled }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, left: 0 });
  const btnRef  = useRef(null);
  const menuRef = useRef(null);

  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 208) }); // 208 = w-52
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey    = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => (open ? setOpen(false) : openMenu())}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        title="More actions"
        className="w-8 h-8 flex items-center justify-center rounded-lg border border-divider text-muted hover:text-secondary hover:bg-nav-hover disabled:opacity-40 transition-colors"
      >
        <MdMoreHoriz className="text-lg" />
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          style={{ top: pos.top, left: pos.left }}
          className="fixed z-50 w-52 bg-card border border-divider rounded-lg shadow-xl py-1"
        >
          {items.map((it, i) => (
            <button
              key={i}
              role="menuitem"
              onClick={() => { setOpen(false); it.onClick(); }}
              disabled={it.disabled}
              title={it.title}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                it.danger ? 'text-red-500' : 'text-secondary'
              }`}
            >
              <it.Icon className="text-base flex-shrink-0" /> {it.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ── Tab: History (read-only — only what has actually been paid) ────────────────
// No Record Payment buttons, no unpaid rows — that job belongs to the Collect tab. Fetches
// completed + partial fees only, so a partial fee's already-made installments still show up
// here (even though its remaining balance also appears in Collect) while a never-paid fee
// (pending/overdue) never does.

function HistoryTab({ initialSearch }) {
  const v                           = useClubVocab();
  const { formatMonth }              = useCalendar();
  // Fixed to paid + partial — read-only ledger of money actually received. No status picker:
  // there is nothing else to toggle to (unpaid rows live in Collect, waived fees in Receivables'
  // Written-Off archive).
  const HISTORY_STATUS = 'completed,partial';
  const [methodFilter,  setMethod]  = useState('');
  // List-filter month — '' means All months (default).
  const [monthFilter,   setMonthFilter] = useState('');
  const [search,        setSearch]  = useState(initialSearch || '');
  const [searchInput,   setSearchInput] = useState(initialSearch || '');
  const [page,          setPage]    = useState(1);
  const [limit,         setLimit]   = useState(10);
  const [sortBy,        setSortBy]  = useState('createdAt');
  const [sortOrder,     setSortOrder] = useState('desc');
  const [receiptModal,  setReceipt] = useState(null); // paymentId
  const [expandedRows,  setExpanded] = useState(() => new Set()); // feePaymentIds showing installments
  const debounceRef = useRef(null);

  const toggleExpanded = (id) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const { data, isPending, error } = useQuery({
    queryKey: ['fee-payments', 'history', monthFilter, methodFilter, search, page, limit, sortBy, sortOrder],
    queryFn:  () => feeSvc.getAllPayments({
      month: monthFilter || undefined, status: HISTORY_STATUS,
      method: methodFilter || undefined,
      search: search || undefined,
      page, limit, sortBy, sortOrder,
    }).then(r => r.data),
    placeholderData: keepPreviousData,
  });

  const handleSearch = (val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val); setPage(1); }, 400);
  };

  const payments = data?.payments || data?.data || [];
  const total    = data?.pagination?.total ?? data?.total ?? payments.length;
  const pages    = data?.pagination?.totalPages ?? data?.pages ?? 1;

  // If an action empties the current page, step back one page
  useEffect(() => {
    if (!isPending && total > 0 && payments.length === 0 && page > 1) {
      setPage(p => Math.max(1, p - 1));
    }
  }, [isPending, total, payments.length, page]);

  // Club access blocked (subscription suspended/expired) — clear panel, not empty list.
  if (isClubSuspended(error)) return <SuspendedAccessPanel />;
  if (isClubCancelled(error)) return <SuspendedAccessPanel cancelled />;

  return (
    <div className="space-y-4">
      {/* Filters — read-only ledger, no status picker (fixed to paid + partial) */}
      <div className="bg-card border border-divider rounded-xl p-3 space-y-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium text-secondary">Payments received</span>
          <span className="ml-auto text-xs text-muted">{total} record{total !== 1 ? 's' : ''}</span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
            <input
              value={searchInput}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-card border border-divider text-secondary placeholder-subtle focus:outline-none focus:border-secondary transition-colors"
            />
          </div>
          {/* Month filter — '' shows all months (default). A clear button returns to All months. */}
          <div className="flex items-center gap-1">
            <DualDatePicker monthOnly value={monthFilter} onChange={v => { setMonthFilter(v); setPage(1); }} />
            {monthFilter ? (
              <button
                type="button"
                onClick={() => { setMonthFilter(''); setPage(1); }}
                title="Show all months"
                className="h-10 inline-flex items-center gap-1 px-2.5 rounded-lg border border-divider text-muted hover:text-secondary hover:bg-nav-hover transition-colors text-sm"
              >
                <MdClose className="text-base" /> All months
              </button>
            ) : (
              <span className="text-xs text-muted px-1 whitespace-nowrap">All months</span>
            )}
          </div>
          <div className="relative min-w-[150px]">
            <select
              value={methodFilter}
              onChange={e => { setMethod(e.target.value); setPage(1); }}
              className="w-full appearance-none rounded-xl pl-3 pr-9 py-2 text-sm bg-card border border-divider text-secondary focus:outline-none focus:border-secondary"
            >
              <option value="">All methods</option>
              <option value="cash">Cash</option>
              <option value="bank">Bank Transfer</option>
              <option value="esewa">eSewa</option>
              <option value="khalti">Khalti</option>
              <option value="fonepay">Fonepay</option>
              <option value="other">Other</option>
            </select>
            <MdExpandMore className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
          </div>
          {/* Sort */}
          <div className="relative min-w-[150px]">
            <select
              value={`${sortBy}:${sortOrder}`}
              onChange={e => { const [b, o] = e.target.value.split(':'); setSortBy(b); setSortOrder(o); setPage(1); }}
              className="w-full appearance-none rounded-xl pl-3 pr-9 py-2 text-sm bg-card border border-divider text-secondary focus:outline-none focus:border-secondary"
            >
              <option value="createdAt:desc">Newest first</option>
              <option value="createdAt:asc">Oldest first</option>
              <option value="amount:desc">Amount (high–low)</option>
              <option value="amount:asc">Amount (low–high)</option>
              <option value="status:asc">Status (A–Z)</option>
            </select>
            <MdExpandMore className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
          </div>
          {/* Rows per page */}
          <div className="relative min-w-[110px]">
            <select
              value={limit}
              onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
              className="w-full appearance-none rounded-xl pl-3 pr-9 py-2 text-sm bg-card border border-divider text-secondary focus:outline-none focus:border-secondary"
              title="Rows per page"
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <MdExpandMore className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-divider rounded-xl overflow-hidden">

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-white/[0.06]">
          {isPending ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-4 space-y-2">
                <div className="h-4 w-32 bg-nav-hover rounded animate-pulse" />
                <div className="h-3 w-20 bg-nav-hover rounded animate-pulse" />
              </div>
            ))
          ) : !payments.length ? (
            <div className="py-14 text-center px-4">
              <MdPayments className="text-4xl text-subtle mx-auto mb-3" />
              <p className="text-sm font-semibold text-secondary mb-1">No payments received yet</p>
              <p className="text-xs text-muted">Money collected from the Collect tab shows up here once recorded.</p>
            </div>
          ) : payments.map(p => {
            const hasInstallments = Array.isArray(p.payments) && p.payments.length > 1;
            const isExpanded = expandedRows.has(p._id);
            return (
            <div key={p._id} className="p-4 hover:bg-nav-hover transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-secondary leading-tight">{p.studentId?.name || '—'}</p>
                  <p className="text-[11px] text-muted mt-0.5">
                    {p.studentProfile?.studentId && <span className="font-mono">{p.studentProfile.studentId} · </span>}
                    {p.type === 'custom' ? (p.description || 'Custom charge') : formatMonth(p.month)}
                    {p.isProrated && p.billableDays ? ` (${p.billableDays} days)` : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-base font-bold text-secondary">NPR {(p.totalAmount || p.amount)?.toLocaleString()}</p>
                  {p.isProrated && (
                    <span className="inline-block text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 mt-0.5">Partial</span>
                  )}
                  {p.discount > 0 && <p className="text-[10px] text-emerald-600">Discount NPR {p.discount.toLocaleString()}</p>}
                  {p.status === 'partial' && (
                    <p className="text-[10px] text-muted">Paid NPR {(p.amountPaid || 0).toLocaleString()} · NPR {(p.balance || 0).toLocaleString()} left</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[p.status] || STATUS_BADGE.pending}`}>
                  {STATUS_LABEL[p.status] || p.status}
                </span>
                {p.method && (
                  <span className="text-xs text-muted">{METHOD_LABEL[p.method] || p.method}</span>
                )}
                {(p.paidDate || p.paidAt) && (
                  <span className="text-xs text-muted"><DualDate date={p.paidDate || p.paidAt} /></span>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setReceipt(p._id)}
                  className="text-xs font-medium px-3 py-1.5 rounded-full bg-card border border-divider text-secondary hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200"
                >
                  Receipt
                </button>
                {(p.transferScreenshot || p.proofUrl) && (
                  <a href={p.transferScreenshot || p.proofUrl} target="_blank" rel="noreferrer" title="View payment proof"
                    className="inline-flex items-center gap-1 text-xs font-medium text-muted hover:text-secondary transition-colors">
                    <MdOpenInNew className="text-sm" /> Proof
                  </a>
                )}
                {hasInstallments && (
                  <button
                    onClick={() => toggleExpanded(p._id)}
                    className="text-xs font-medium text-muted hover:text-secondary transition-colors"
                  >
                    {isExpanded ? 'Hide installments' : `Show ${p.payments.length} installments`}
                  </button>
                )}
              </div>
              {hasInstallments && isExpanded && (
                <div className="mt-2">
                  <InstallmentList payments={p.payments} totalAmount={p.totalAmount ?? p.amount ?? 0} />
                </div>
              )}
            </div>
          );})}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="bg-card border-b border-divider text-xs text-muted font-semibold uppercase tracking-wide">
                <th className="text-left px-5 py-3">{v.member}</th>
                <th className="text-left px-4 py-3">Member ID</th>
                <th className="text-left px-4 py-3">Belt</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Method</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Paid Date</th>
                <th className="text-left px-4 py-3">Receipt #</th>
                <th className="text-left px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-divider">
                    {Array.from({ length: 9 }).map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-nav-hover rounded animate-pulse" style={{ width: j === 0 ? 120 : 60 }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : !payments.length ? (
                <tr>
                  <td colSpan={9} className="text-center py-14">
                    <MdPayments className="text-4xl text-subtle mx-auto mb-3" />
                    <p className="text-sm font-semibold text-secondary mb-1">No payments received yet</p>
                    <p className="text-xs text-muted">Money collected from the Collect tab shows up here once recorded.</p>
                  </td>
                </tr>
              ) : payments.map(p => {
                const hasInstallments = Array.isArray(p.payments) && p.payments.length > 1;
                const isExpanded = expandedRows.has(p._id);
                return (
                <Fragment key={p._id}>
                <tr className="border-b border-divider last:border-0 hover:bg-nav-hover transition-colors">
                  <td className="px-5 py-3">
                    <p className="text-sm font-medium text-secondary">{p.studentId?.name || '—'}</p>
                    <p className="text-xs text-muted">{p.studentId?.email}</p>
                    {p.type === 'custom' ? (
                      <p className="text-[11px] text-muted inline-flex items-center gap-1.5">
                        <span>{p.description || 'Custom charge'}</span>
                        <span className="px-1.5 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wide bg-nav-hover text-muted border border-divider">Custom</span>
                      </p>
                    ) : (
                      <p className="text-[11px] text-muted">
                        {formatMonth(p.month)}
                        {p.isProrated && p.billableDays ? ` (${p.billableDays} days)` : ''}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-muted">
                    {p.studentProfile?.studentId || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted">
                    {p.studentProfile?.currentGrade || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-secondary">
                      NPR {(p.totalAmount || p.amount)?.toLocaleString()}
                    </p>
                    {p.isProrated && (
                      <span className="inline-block text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 mt-0.5">Partial</span>
                    )}
                    {p.discount > 0 && (
                      <p className="text-[10px] text-emerald-600" title={p.discountInfo?.note || undefined}>Discount NPR {p.discount.toLocaleString()}</p>
                    )}
                    {p.status === 'partial' && (
                      <p className="text-[10px] text-muted">Paid NPR {(p.amountPaid || 0).toLocaleString()} · NPR {(p.balance || 0).toLocaleString()} left</p>
                    )}
                    {p.notes && (
                      <p className="text-[10px] text-muted mt-0.5 leading-tight max-w-[200px]">{p.notes}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted">{METHOD_LABEL[p.method] || p.method || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[p.status] || STATUS_BADGE.pending}`}>
                      {STATUS_LABEL[p.status] || p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted">
                    {p.paidDate || p.paidAt ? <DualDate date={p.paidDate || p.paidAt} /> : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-muted">
                    {p.receiptNumber || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setReceipt(p._id)}
                        className="text-xs font-medium px-3 py-1.5 rounded-full bg-card border border-divider text-secondary hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200"
                      >
                        Receipt
                      </button>
                      {(p.transferScreenshot || p.proofUrl) && (
                        <a href={p.transferScreenshot || p.proofUrl} target="_blank" rel="noreferrer" title="View payment proof"
                          className="inline-flex items-center gap-1 text-xs font-medium text-muted hover:text-secondary transition-colors">
                          <MdOpenInNew className="text-sm" /> Proof
                        </a>
                      )}
                      {hasInstallments && (
                        <button
                          onClick={() => toggleExpanded(p._id)}
                          className="text-xs font-medium text-muted hover:text-secondary transition-colors"
                        >
                          {isExpanded ? 'Hide installments' : `${p.payments.length} installments`}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {hasInstallments && isExpanded && (
                  <tr className="border-b border-divider last:border-0">
                    <td colSpan={9} className="px-5 pb-3 pt-0 bg-nav-hover/30">
                      <InstallmentList payments={p.payments} totalAmount={p.totalAmount ?? p.amount ?? 0} />
                    </td>
                  </tr>
                )}
                </Fragment>
              );})}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-divider flex-wrap gap-2">
            <p className="text-xs text-muted">
              Showing <span className="font-medium text-secondary">{(page - 1) * limit + 1}–{Math.min(page * limit, total)}</span> of{' '}
              <span className="font-medium text-secondary">{total}</span> · Page {page} of {pages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="text-xs font-medium px-3 py-1.5 rounded-full bg-card border border-divider text-secondary hover:bg-secondary hover:text-card hover:border-secondary disabled:opacity-40 transition-all duration-200"
              >
                ← Prev
              </button>
              <button
                onClick={() => setPage(p => Math.min(pages, p + 1))}
                disabled={page === pages}
                className="text-xs font-medium px-3 py-1.5 rounded-full bg-card border border-divider text-secondary hover:bg-secondary hover:text-card hover:border-secondary disabled:opacity-40 transition-all duration-200"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {receiptModal && (
        <ReceiptModal paymentId={receiptModal} onClose={() => setReceipt(null)} />
      )}
    </div>
  );
}

// ── Tab: Proofs to Review ─────────────────────────────────────────────────────

function RejectModal({ payment, onClose, onSuccess }) {
  useEscapeKey(onClose);
  const v = useClubVocab();
  const { isReadOnly } = useImpersonation();
  const [reason, setReason] = useState('');
  const mutation = useMutation({
    mutationFn: () => feeSvc.rejectProof(payment._id, reason),
    onSuccess:  () => { toast.success('Proof rejected'); onSuccess(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to reject'),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider">
          <h2 className="font-bold text-secondary text-base">Reject Proof</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover"><MdClose /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-muted">
            Reject proof for <strong>{payment.studentId?.name}</strong> — {payment.month}?
          </p>
          <div>
            <label className={LABEL_CLS}>Reason (required — shown to {v.member.toLowerCase()})</label>
            <input
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Screenshot is blurry, wrong amount…"
              className={INPUT_CLS}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-divider">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !reason.trim() || isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? 'Rejecting…' : 'Reject Proof'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProofsTab() {
  const qc                  = useQueryClient();
  const v                   = useClubVocab();
  const { isReadOnly }      = useImpersonation();
  const { formatDate, formatMonth } = useCalendar();
  const [rejectTarget,  setRejectTarget]  = useState(null);
  const [approvingId,   setApprovingId]   = useState(null);
  const [confirm, confirmModal] = useConfirm();

  const { data, isPending, refetch } = useQuery({
    queryKey: ['fee-proofs'],
    queryFn:  () => feeSvc.getProofsForReview().then(r => r.data),
  });

  const payments = data?.payments || [];

  const handleApprove = async (p) => {
    setApprovingId(p._id);
    try {
      let r;
      try {
        r = await feeSvc.approveProof(p._id, {});
      } catch (e) {
        // The claimed amount exceeds this fee's balance — not an overpayment, it settles the
        // next month(s) forward. Show the preview and let the admin confirm before it's applied.
        if (e.response?.status === 409 && e.response?.data?.code === 'PAYMENT_EXCEEDS_BALANCE') {
          const ok = await confirm({
            title: 'Apply the extra to next month?',
            message: e.response.data.message,
            confirmLabel: 'Approve',
            destructive: false,
          });
          if (!ok) { setApprovingId(null); return; }
          r = await feeSvc.approveProof(p._id, { confirmForwardSettlement: true });
        } else {
          throw e;
        }
      }
      toast.success(r.data.message);
      qc.invalidateQueries({ queryKey: ['fee-payments'] });
      qc.invalidateQueries({ queryKey: ['fee-summary'] });
      qc.invalidateQueries({ queryKey: ['students'] });
      qc.invalidateQueries({ queryKey: ['fee-proofs'] });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to approve');
    } finally {
      setApprovingId(null);
    }
  };

  const onRejectSuccess = () => {
    setRejectTarget(null);
    qc.invalidateQueries({ queryKey: ['fee-proofs'] });
    qc.invalidateQueries({ queryKey: ['fee-payments'] });
  };

  if (isPending) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-20 bg-nav-hover animate-pulse rounded-2xl" />)}
      </div>
    );
  }

  if (!payments.length) {
    return (
      <Card className="text-center py-14">
        <MdCheckCircle className="text-4xl text-green-300 mx-auto mb-3" />
        <p className="text-sm font-semibold text-muted">No proofs waiting for review</p>
        <p className="text-xs text-subtle mt-1">{v.members} will appear here once they upload payment proof</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">{payments.length} proof{payments.length !== 1 ? 's' : ''} waiting</p>
        <Button variant="ghost" onClick={refetch}><MdRefresh /> Refresh</Button>
      </div>

      <div className="space-y-3">
        {payments.map(p => (
          <Card key={p._id}>
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              {/* Proof image / file */}
              {p.proofUrl && (
                <div className="flex-shrink-0">
                  {p.proofMimeType?.startsWith('image/') ? (
                    <a href={p.proofUrl} target="_blank" rel="noreferrer">
                      <img
                        src={p.proofUrl}
                        alt="Payment proof"
                        className="w-24 h-24 object-cover rounded-xl border border-divider hover:opacity-90 transition-opacity"
                      />
                    </a>
                  ) : (
                    <a
                      href={p.proofUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex flex-col items-center justify-center w-24 h-24 bg-nav-hover rounded-xl border border-divider text-xs text-muted hover:bg-nav-hover transition-colors"
                    >
                      <MdOpenInNew className="text-2xl text-subtle mb-1" />
                      View PDF
                    </a>
                  )}
                </div>
              )}

              {/* Details */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <p className="font-semibold text-secondary">{p.studentId?.name || '—'}</p>
                  <span className="text-xs text-subtle font-mono">{p.studentProfile?.studentId || ''}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE.proof_submitted}`}>
                    Proof Submitted
                  </span>
                  {/* Multi-month submission — one screenshot covering several months, approved as a unit. */}
                  {(p.groupCount || 1) > 1 && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-nav-hover text-muted border border-divider">
                      {p.groupCount} months
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs text-muted mt-2">
                  <div className={(p.groupCount || 1) > 1 ? 'col-span-2 sm:col-span-3' : ''}>
                    <span className="text-subtle">{(p.groupCount || 1) > 1 ? 'Months:' : 'Month:'}</span>{' '}
                    <span className="font-medium text-secondary">
                      {(p.groupMonths?.length ? p.groupMonths : [p.month]).map(formatMonth).join(', ')}
                    </span>
                  </div>
                  <div><span className="text-subtle">Method:</span> <span className="font-medium">{METHOD_LABEL[p.proofMethod || p.method] || p.proofMethod || p.method}</span></div>
                  <div>
                    <span className="text-subtle">{(p.groupCount || 1) > 1 ? 'Total:' : 'Amount:'}</span>{' '}
                    <span className="font-medium">NPR {(p.groupTotal ?? p.proofAmount ?? p.amount)?.toLocaleString()}</span>
                  </div>
                  {p.proofDate && <div><span className="text-subtle">Paid on:</span> <span><DualDate date={p.proofDate} /></span></div>}
                  {p.proofReference && <div><span className="text-subtle">Reference:</span> <span className="font-mono">{p.proofReference}</span></div>}
                  <div><span className="text-subtle">Submitted:</span> <span><DualDate date={p.proofSubmittedAt} /></span></div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => handleApprove(p)}
                  disabled={approvingId === p._id || isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <MdThumbUp className="text-base" />
                  {approvingId === p._id ? 'Approving…' : 'Approve'}
                </button>
                <button
                  onClick={() => setRejectTarget(p)}
                  disabled={isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MdThumbDown className="text-base" />
                  Reject
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {rejectTarget && (
        <RejectModal payment={rejectTarget} onClose={() => setRejectTarget(null)} onSuccess={onRejectSuccess} />
      )}
      {confirmModal}
    </div>
  );
}


// ── Main component ────────────────────────────────────────────────────────────

// Tab ↔ URL (?tab=…) mapping so the active tab survives a refresh. 'overview' is the
// default and carries no param.
const TAB_TO_URL = { overview: '', collect: 'collect', history: 'history', proofs: 'proofs' };
const URL_TO_TAB = { collect: 'collect', history: 'history', proofs: 'proofs', overview: 'overview' };

export default function Payments() {
  document.title = 'Monthly Fees — TrainAxis';
  const v = useClubVocab();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  // ?search= forces the History tab; otherwise honour ?tab, else Overview. (Receivables now
  // opens the Record Payment modal in place instead of deep-linking here — see Receivables.jsx.)
  const [tab, setTab] = useState(
    initialSearch ? 'history' : (URL_TO_TAB[searchParams.get('tab')] || 'overview')
  );
  const [month, setMonth] = useState(THIS_MONTH);

  // Persist the active tab to the URL (replace, so the back button stays sane).
  const changeTab = (key) => {
    setTab(key);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (TAB_TO_URL[key]) next.set('tab', TAB_TO_URL[key]);
      else                 next.delete('tab');
      return next;
    }, { replace: true });
  };

  const { data: proofsData } = useQuery({
    queryKey: ['fee-proofs-count'],
    queryFn:  () => feeSvc.getProofsForReview().then(r => r.data),
    refetchInterval: 60000,
  });
  const proofCount = proofsData?.total || 0;

  const TABS = [
    { key: 'overview', label: 'Overview', Icon: MdBarChart },
    { key: 'collect',  label: 'Collect',  Icon: MdPayments },
    { key: 'history',  label: 'History',  Icon: MdReceipt  },
    { key: 'proofs',   label: 'Proofs to Review', Icon: MdUpload },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Monthly Fees</h1>
          <p className="text-sm text-subtle mt-0.5">Track and collect monthly fees from full {v.members.toLowerCase()}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-divider">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => changeTab(key)}
            className={`flex items-center gap-2 px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-all ${
              tab === key ? 'text-secondary border-secondary' : 'text-muted border-transparent hover:text-secondary'
            }`}
          >
            <Icon className="text-base" />
            {label}
            {key === 'proofs' && proofCount > 0 && (
              <span className="ml-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                {proofCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab month={month} onMonthChange={setMonth} />}
      {tab === 'collect'  && <CollectTab />}
      {tab === 'history'  && <HistoryTab initialSearch={initialSearch} />}
      {tab === 'proofs'   && <ProofsTab />}
    </div>
  );
}
